import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Divider,
  TextField,
  Stack,
  Box,
  Typography,
  Icon,
  Chip,
  Card,
  CardMedia,
  CardContent,
  Button,
  CardActionArea,
  Modal,
  Checkbox,
} from "@mui/material";
import {
  BusinessCenter as BusinessCenterIcon,
  InfoIcon,
  LogoutOutlined,
  AccountCircle,
  ArrowBack,
  Check,
  Clear as ClearIcon,
  EditNote,
  Close,
  ZoomOutMap as ZoomOutMapIcon,
  CurrencyRubleRounded,
} from "@mui/icons-material";

import axios from "axios";
import _ from "lodash";
import { AuthContext } from "../index";
import getLPTheme from "../getLPTheme";
import { useContext, useEffect, useState, useRef } from "react";

import ItemsListV2 from "./ItemsListV2";
import App from "./App";
import { getImageV3 } from "../utils/images";
import { getDay, getHourMinutes, now, plusDays } from "../utils/dateTime";
import SwipableImageCarousel from "./SwipableImageCarousel";
import BonusProgramMassageSpecialist from "./BonusProgramMassageSpecialist";
import { default as BonusProgram } from "@mui/material/Modal";

const AccountScreen = () => {
  const { onLogout, id, userId, role, phoneNumber } = useContext(AuthContext);

  // state
  const [services, setServices] = useState({
    isLoaded: false,
    data: [],
  });
  const DEFAULT_SERVICE_LIST_STATE = {
    open: false,
  };
  const [serviceList, setServiceList] = useState(DEFAULT_SERVICE_LIST_STATE);

  const DEFAULT_SERVICE_DETAILED_STATE = {
    data: null,
  };
  const [serviceDetailed, setServiceDetailed] = useState(
    DEFAULT_SERVICE_DETAILED_STATE,
  );

  const DEFAULT_SERVICE_STATE = {};
  const [service, setService] = useState(DEFAULT_SERVICE_STATE);
  const DEFAULT_SERVICE_EDIT_STATE = {
    // flags
    isCancelling: false,
    isEditing: false,
    isUpdating: false,
    //errors
    //data
    data: {},
  };
  const [serviceEdit, setServiceEdit] = useState(DEFAULT_SERVICE_EDIT_STATE);

  const [mapDetailed, setMapDetailed] = useState(null);

  const SPECIALIST_INFO_DEFAULT_STATE = {
    isLoaded: false,
    data: {
      firstName: "",
      lastName: ""
    }
  };

  const SPECIALIST_INFO_EDIT_DEFAULT_STATE = {
    isEditing: false,
    isUpdating: false,
    data: {
      firstName: "",
      lastName: "",
    }
  }
  const [specialistInfo, setSpecialistInfo] = useState(
    SPECIALIST_INFO_DEFAULT_STATE,
  );
  const [specialistEdit, setSpecialistEdit] = useState(
    SPECIALIST_INFO_EDIT_DEFAULT_STATE,
  );
  // state end

  const BONSUS_PROGRAM_DEFAULT_STATE = {
    open: false,
  };
  const [bonusProgram, setBonusProgram] = useState(
    BONSUS_PROGRAM_DEFAULT_STATE,
  );

  async function loadServices() {
    // const [ serviceTemplates, allLocations ] = await Promise.all([loadServiceTemplates(), loadAllLocations()]);

    try {
      const resp = await axios.post(`/api/v1/services/get`, {
        clientIds: [id],
      });
      setServices({ isLoaded: true, data: resp.data }); //fillServices(resp.data, serviceTemplates, allLocations)});
    } catch (err) {
      throw err;
    }
  }

  useEffect(() => {
    if (services.isLoaded) {
      return;
    }

    loadServices();
  }, [services.isLoaded]);

  async function loadSpecialist() {
    try {
      const resp = await axios.post(`/api/v1/specialists/get-specialists`, {
        userId,
      });

      setSpecialistInfo((prevState) => ({
        isLoaded: true,
        data: resp?.data,
      }));
    } catch (err) {
      // noop
    }
  }

  useEffect(() => {
    if (specialistInfo.isLoaded || !userId) {
      return;
    }

    loadSpecialist();
  }, [specialistInfo.isLoaded, userId]);

  // handlers
  const handleServicePick = (item) => {
    setServiceDetailed((prevState) => ({ ...prevState, data: item }));
  };

  const handleCancelService = async (service) => {
    try {
      setServiceEdit((prevState) => ({ ...prevState, isCancelling: true }));

      const resp = await axios.put(`/api/v1/services`, {
        id: service?.id,
        client: {
          id: null,
        },
      });

      setServiceDetailed(null);
      setServices((prevState) => ({ ...prevState, isLoaded: false }));
    } catch (err) {
      throw err;
    }
    setServiceEdit((prevState) => ({ ...prevState, isCancelling: false }));
  };

  const handleServiceChange = async () => {
    try {
      setServiceEdit((prevState) => ({ ...prevState, isUpdating: true }));
      const resp = await axios.put(`/api/v1/services`, serviceEdit?.data);
    } catch (err) {
      //noop
    }
    setServiceEdit((prevState) => ({
      ...prevState,
      isUpdating: false,
      isEditing: false,
    }));
    const detailedServiceId = serviceEdit?.data?.id;
    setServices((prevState) => ({ ...prevState, isLoaded: false }));
    setServiceDetailed({ data: null });
    //todo think how to setup detailed service after change
  };

  const handleSpecialistChange = async () => {
    setSpecialistEdit((prevState) => ({ ...prevState, isUpdating: true }));

    try {
      const resp = await axios.patch(`/api/v1/specialists`, {
        id: _.head(specialistInfo?.data)?.id,
        firstName: specialistEdit?.data?.firstName,
        lastName: specialistEdit?.data?.lastName,
      });
    } catch (err) {
      throw err;
    }

    setSpecialistEdit((prevState) => ({
      ...prevState,
      isUpdating: false,
      isEditing: false,
    }));
    setSpecialistInfo((prevState) => ({ ...prevState, isLoaded: false }));
  };

  const handleSpecialistEditClick = () => {
    setSpecialistEdit((prevState) => ({
      ...prevState,
      isEditing: true,
      data: {
        firstName: _.head(specialistInfo?.data)?.firstName || "",
        lastName: _.head(specialistInfo?.data)?.lastName || "",
      },
    }));
  };

  const handleServiceEdit = () => {
    setServiceEdit((prevState) => {
      return {
        ...prevState,
        isEditing: true,
        data: { ...serviceDetailed.data },
      };
    });
  };

  const handleBonusProgramOpen = () => {
    setBonusProgram((prevState) => ({ ...prevState, open: true }));
  };

  const handleBonusProgramClose = () => {
    setBonusProgram((prevState) => ({ ...prevState, open: false }));
  };

  // handlers end

  return (
    <App withNavigation>
      {/* main account view */}
      <Box
        sx={{
          backgroundColor: "rgb(0 0 0/0.05)",
          height: "100%",
        }}
      >
        <Box
          sx={{
            borderRadius: "20px",
            backgroundColor: "rgb(255 255 255)",
            mb: "10px",
            pb: "5px",
          }}
        >
          <Stack
            direction={"row"}
            sx={{ justifyContent: "flex-end", width: "100%" }}
          >
            <EditNote
              onClick={() => handleSpecialistEditClick()}
              sx={{
                display: "block",
                fontSize: "35px",
                p: 2,
                color: "rgb(0 0 0/0.3)",
              }}
            />
          </Stack>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "120px",
            }}
          >
            <AccountCircle
              sx={{ fontSize: "100px", color: "rgb(0 0 0/0.15)" }}
            />
          </Box>
          <Box sx={{ p: 2 }}>
            <Typography
              align={"center"}
              sx={{ fontWeight: "bold", fontSize: "20px", color: "#777" }}
            >
              {`${_.head(specialistInfo?.data)?.firstName ?? "Имя"} ${_.head(specialistInfo?.data)?.lastName ?? "Фамилия"}`}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: "14px", color: "rgb(0 0 0/0.75)", mb: 1 }}
              align={"center"}
            >
              {phoneNumber}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            borderRadius: "20px",
            backgroundColor: "rgb(255 255 255)",
            pt: "10px",
            pb: "10px",
          }}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            sx={{
              pt: 1,
              pr: 2,
              pl: 1,
              pb: 1,
            }}
            onClick={handleBonusProgramOpen}
          >
            <CurrencyRubleRounded sx={{ p: 2, color: "rgb(0 0 0/0.75)" }} />
            <Typography fontSize={"18px"} sx={{ color: "rgb(0 0 0/ 0.7)" }}>
              Бонусная программа
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            sx={{
              pt: 1,
              pr: 2,
              pl: 1,
              pb: 1,
            }}
            onClick={onLogout}
          >
            <LogoutOutlined sx={{ p: 2, color: "rgb(0 0 0/0.75)" }} />
            <Typography fontSize={"18px"} sx={{ color: "rgb(0 0 0/ 0.7)" }}>
              Выйти
            </Typography>
          </Stack>
        </Box>
      </Box>
      {/* visits modal */}
      <Modal
        open={serviceList?.open}
        sx={{
          overflow: "auto",
          height: "100vh",
          zIndex: 1305,
        }}
      >
        <Box
          sx={{
            height: "100%",
            backgroundColor: "#fff",
          }}
        >
          <Stack
            sx={{
              justifyContent: "space-between",
              margin: 0,
              height: "100dvh",
            }}
          >
            <Box>
              <Stack direction={"row"} sx={{ justifyContent: "flex-end" }}>
                <Icon
                  onClick={() => {
                    setServiceList((prevState) => ({
                      ...prevState,
                      open: false,
                    }));
                  }}
                  sx={{
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor: "#000",
                      color: "#fff",
                    },
                    p: "3px",
                    backgroundColor: "#eee",
                    borderRadius: "50%",
                    m: 1,
                  }}
                >
                  <ClearIcon />
                </Icon>
              </Stack>
            </Box>
            <Box sx={{ overflowY: "auto", height: "100%" }}>
              {false && (
                <ItemsListV2
                  itemsLoading={!services.isLoaded}
                  items={
                    services?.data?.length ? (
                      services?.data
                        ?.sort((a, b) => b.timeFrame?.from - a.timeFrame?.from)
                        .map((item) => (
                          <Card
                            elevation={1}
                            key={item.id}
                            sx={{
                              display: "flex",
                              "&:hover": { border: "1px solid #2999ff" },
                              "&:focus": { outline: "none" },
                            }}
                            onClick={() => {
                              handleServicePick(item);
                            }}
                          >
                            <CardActionArea
                              disableRipple
                              sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "flex-start",
                              }}
                              onClick={() => {
                                handleServicePick(item);
                              }}
                            >
                              <CardMedia
                                component="img"
                                sx={{
                                  width: 100,
                                  height: 100,
                                  p: 1,
                                  borderRadius: "15px",
                                  ...(now() > item?.timeFrame?.to && {
                                    opacity: "0.5",
                                  }),
                                }}
                                image={getImageV3(
                                  item?.serviceTemplate?.images?.map(
                                    (im) => im.id,
                                  )[0],
                                )}
                                alt=""
                              />
                              <CardContent
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "flex-start",
                                  height: "100%",
                                  width: "100%",
                                  ...(now() > item?.timeFrame?.to && {
                                    color: "#999",
                                  }),
                                }}
                              >
                                {now() > item?.timeFrame?.to ? (
                                  <Typography
                                    sx={{ fontSize: "12px" }}
                                    align={"left"}
                                  >
                                    прошедший{" "}
                                  </Typography>
                                ) : null}
                                <Typography align={"left"}>
                                  {`Визит ${getDay(item?.timeFrame?.from)} в ${getHourMinutes(item?.timeFrame?.from)}`}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: "16px" }}
                                  align={"left"}
                                >
                                  {" "}
                                  {item?.serviceTemplate?.title}
                                </Typography>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        ))
                    ) : (
                      <Typography sx={{ color: "gray", fontSize: "14px" }}>
                        У вас пока нет актуальных сервисов
                      </Typography>
                    )
                  }
                  itemDetailed={
                    <>
                      <div>test</div>
                      {serviceDetailed?.data ? (
                        <Card
                          elevation={1}
                          // sx={{display: 'block', minHeight: '100vh'}}
                          sx={{
                            display: "flex",
                            height: "100dvh",
                            flexDirection: "column",
                          }}
                        >
                          <Box sx={{ minHeight: "100vh" }}>
                            <CardMedia
                              sx={{ height: "200px" }}
                              component="img"
                              image={getImageV3(
                                serviceDetailed?.data?.serviceTemplate?.images?.map(
                                  (im) => im.id,
                                )[0],
                              )}
                              alt=""
                            />
                            <CardContent>
                              <Typography
                                align="left"
                                sx={{ pb: 2, fontSize: "18px" }}
                              >
                                {" "}
                                {
                                  serviceDetailed?.data?.serviceTemplate?.title
                                }{" "}
                              </Typography>
                              <Typography
                                sx={{ fontSize: "12px" }}
                                align="left"
                              >
                                {" "}
                                {
                                  serviceDetailed?.data?.serviceTemplate
                                    ?.description
                                }{" "}
                              </Typography>
                              <Typography sx={{ pt: 2 }} align={"left"}>
                                Специалист
                              </Typography>
                              <Stack direction={"row"} sx={{ pt: 1 }}>
                                <Chip
                                  size={"small"}
                                  color="success"
                                  icon={<Check />}
                                  label={"Медицинская книжка проверена"}
                                />
                              </Stack>
                              {serviceDetailed?.data?.customizations?.cosmetics
                                ?.enabled ? (
                                <>
                                  <Typography sx={{ pt: 2 }} align={"left"}>
                                    Используемые средства
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: "12px" }}
                                    align={"left"}
                                  >
                                    {
                                      serviceDetailed?.data?.customizations?.cosmetics?.data?.filter(
                                        (it) => it.checked,
                                      )?.[0]?.title
                                    }
                                  </Typography>
                                </>
                              ) : null}

                              {serviceDetailed?.data?.customizations?.wish
                                ?.enabled ? (
                                <>
                                  <Typography sx={{ pt: 2 }} align={"left"}>
                                    Пожелания к процедуре
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: "12px" }}
                                    align={"left"}
                                  >
                                    {
                                      serviceDetailed?.data?.customizations
                                        ?.wish?.data
                                    }
                                  </Typography>
                                </>
                              ) : null}

                              <Typography sx={{ pt: 2 }} align={"left"}>
                                Место
                              </Typography>
                              <Typography
                                sx={{ fontSize: "12px" }}
                                align={"left"}
                              >
                                {serviceDetailed?.data?.location?.address}
                              </Typography>
                              <Typography sx={{ pt: 2 }} align={"left"}>
                                Время
                              </Typography>
                              <Typography
                                sx={{ fontSize: "12px" }}
                                align={"left"}
                              >{`${getDay(serviceDetailed?.data?.timeFrame?.from)} в ${getHourMinutes(serviceDetailed?.data?.timeFrame?.from)}`}</Typography>
                              <Stack
                                direction={"row"}
                                sx={{ justifyContent: "center", mt: 2 }}
                                spacing={2}
                              >
                                {serviceDetailed?.data?.paymentStatus !=
                                  "PAID" &&
                                serviceDetailed?.data?.paymentLink ? (
                                  <Button
                                    variant={"outlined"}
                                    href={serviceDetailed?.data?.paymentLink}
                                    sx={{
                                      flex: "1 1 auto",
                                      maxWidth: "400px",
                                    }}
                                  >
                                    Оплатить
                                  </Button>
                                ) : null}
                                {plusDays(now(), 1) <
                                serviceDetailed?.data?.timeFrame?.from ? (
                                  <Button
                                    variant={"contained"}
                                    onClick={() => {
                                      handleServiceEdit();
                                    }}
                                    sx={{
                                      flex: "1 1 auto",
                                      maxWidth: "300px",
                                    }}
                                  >
                                    Кастомизировать
                                  </Button>
                                ) : null}
                              </Stack>
                              {now() <
                              serviceDetailed?.data?.timeFrame?.from ? (
                                <Stack
                                  sx={{
                                    mt: 1,
                                    alignItems: "center",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  <Button
                                    variant={"text"}
                                    onClick={() => {
                                      handleCancelService(
                                        serviceDetailed?.data,
                                      );
                                    }}
                                    sx={{
                                      flex: "1 1 auto",
                                      maxWidth: "300px",
                                      color: "#cd5c5c",
                                      alignItems: "center",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    {/*<Close sx={{mr: 1}}/>*/}
                                    <Typography
                                      align={"center"}
                                      sx={{ height: "100%" }}
                                    >
                                      Отменить прием
                                    </Typography>
                                  </Button>
                                </Stack>
                              ) : null}
                            </CardContent>
                          </Box>
                        </Card>
                      ) : null}
                    </>
                  }
                  closeDetailed={() => {}}
                />
              )}
              <ItemsListV2
                itemsLoading={!services.isLoaded}
                closeDetailed={() => {
                  setServiceDetailed(DEFAULT_SERVICE_DETAILED_STATE);
                }}
                items={
                  services?.data?.length ? (
                    services?.data
                      ?.sort((a, b) => b.timeFrame?.from - a.timeFrame?.from)
                      .map((item) => (
                        <Card
                          elevation={1}
                          key={item.id}
                          sx={{
                            display: "flex",
                            "&:hover": { border: "1px solid #2999ff" },
                            "&:focus": { outline: "none" },
                          }}
                          onClick={() => {
                            handleServicePick(item);
                          }}
                        >
                          <CardActionArea
                            disableRipple
                            sx={{
                              display: "flex",
                              justifyContent: "flex-start",
                              alignItems: "flex-start",
                            }}
                            onClick={() => {
                              // handleServicePick(item)
                            }}
                          >
                            <CardMedia
                              component="img"
                              sx={{
                                width: 100,
                                height: 100,
                                p: 1,
                                borderRadius: "15px",
                                ...(now() > item?.timeFrame?.to && {
                                  opacity: "0.5",
                                }),
                              }}
                              image={getImageV3(
                                item?.serviceTemplate?.images?.map(
                                  (im) => im.id,
                                )[0],
                              )}
                              alt=""
                            />
                            <CardContent
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-start",
                                height: "100%",
                                width: "100%",
                                ...(now() > item?.timeFrame?.to && {
                                  color: "#999",
                                }),
                              }}
                            >
                              {now() > item?.timeFrame?.to ? (
                                <Typography
                                  sx={{ fontSize: "12px" }}
                                  align={"left"}
                                >
                                  прошедший{" "}
                                </Typography>
                              ) : null}
                              <Typography align={"left"}>
                                {`Визит ${getDay(item?.timeFrame?.from)} в ${getHourMinutes(item?.timeFrame?.from)}`}
                              </Typography>
                              <Typography
                                sx={{ fontSize: "16px" }}
                                align={"left"}
                              >
                                {" "}
                                {item?.serviceTemplate?.title}
                              </Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      ))
                  ) : (
                    <Typography sx={{ color: "gray", fontSize: "14px" }}>
                      У вас пока нет актуальных сервисов
                    </Typography>
                  )
                }
                itemDetailed={
                  serviceDetailed?.data ? (
                    <Card
                      elevation={1}
                      sx={{
                        display: "flex",
                        height: "100dvh",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          justifyContent: "space-between",
                          overflowY: "auto",
                          height: "100%",
                        }}
                      >
                        <SwipableImageCarousel
                          images={serviceDetailed?.data?.serviceTemplate?.images?.map(
                            (it) => getImageV3(it?.id),
                          )}
                          aspectRatio={"4/3"}
                          getImage={getImageV3}
                          imageUrls={serviceDetailed?.data?.serviceTemplate?.images?.map(
                            (it) => getImageV3(it?.id),
                          )}
                          navigation={"fraction"}
                        />
                        <CardContent
                          sx={{
                            position: "relative",
                            marginTop: "-25px",
                            backgroundColor: "#fff",
                            borderTopLeftRadius: "25px",
                            borderTopRightRadius: "25px",
                          }}
                        >
                          <Typography
                            align="center"
                            sx={{ pb: 2, fontSize: "24px" }}
                          >
                            {" "}
                            {serviceDetailed?.data?.serviceTemplate?.title}{" "}
                          </Typography>
                          <Typography
                            align="center"
                            sx={{ fontSize: "12px", color: "gray" }}
                          >
                            {" "}
                            {
                              serviceDetailed?.data?.serviceTemplate
                                ?.description
                            }{" "}
                          </Typography>
                          <Typography
                            sx={{ pt: 6, fontSize: "16px", fontWeight: "bold" }}
                            align={"left"}
                          >
                            Специалист
                          </Typography>
                          <Stack direction={"row"} sx={{ pt: 1 }}>
                            <Chip
                              size={"small"}
                              color="success"
                              icon={<Check />}
                              label={"Медицинская книжка проверена"}
                            />
                          </Stack>
                          {serviceDetailed?.data?.customizations?.cosmetics
                            ?.enabled ? (
                            <>
                              <Typography
                                sx={{
                                  pt: 2,
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                }}
                                align={"left"}
                              >
                                Используемые средства
                              </Typography>
                              <Typography
                                sx={{ fontSize: "12px" }}
                                align={"left"}
                              >
                                {
                                  serviceDetailed?.data?.customizations?.cosmetics?.data?.filter(
                                    (it) => it.checked,
                                  )?.[0]?.title
                                }
                              </Typography>
                            </>
                          ) : null}

                          {serviceDetailed?.data?.customizations?.wish
                            ?.enabled ? (
                            <>
                              <Typography
                                sx={{
                                  pt: 2,
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                }}
                                align={"left"}
                              >
                                Пожелания к процедуре
                              </Typography>
                              <Typography
                                sx={{ fontSize: "12px" }}
                                align={"left"}
                              >
                                {
                                  serviceDetailed?.data?.customizations?.wish
                                    ?.data
                                }
                              </Typography>
                            </>
                          ) : null}

                          <Typography
                            sx={{ pt: 2, fontSize: "16px", fontWeight: "bold" }}
                            align={"left"}
                          >
                            Место
                          </Typography>
                          <Typography sx={{ fontSize: "12px" }} align={"left"}>
                            {serviceDetailed?.data?.location?.address}
                          </Typography>

                          <Box sx={{ mt: 2 }}>
                            <ItemsListV2
                              items={[
                                <Box sx={{ position: "relative" }}>
                                  <Box
                                    sx={{
                                      // aspectRatio: '3/2',
                                      width: "100%",
                                      borderRadius: "15px",
                                      mt: 2,
                                    }}
                                    onClick={() => {
                                      setMapDetailed(true);
                                    }}
                                    component={"img"}
                                    alt={""}
                                    // src={ getImage(locationDetailed?.images?.map(im => im.id)) }
                                    src={
                                      serviceDetailed?.data?.location?.images?.map(
                                        (it) => getImageV3(it?.id),
                                      )?.[0]
                                    }
                                  />
                                  <ZoomOutMapIcon
                                    onClick={() => setMapDetailed(true)}
                                    sx={{
                                      "&:hover": {
                                        cursor: "pointer",
                                        backgroundColor: "#000",
                                        color: "#fff",
                                      },
                                      p: "5px",
                                      backgroundColor: "#fff",
                                      borderRadius: "12px",
                                      zIndex: 1,
                                      position: "absolute",
                                      top: "25px",
                                      right: "10px",
                                    }}
                                  />
                                </Box>,
                              ]}
                              itemsLoading={false}
                              closeDetailed={() => {
                                setMapDetailed(null);
                              }}
                              itemDetailed={
                                mapDetailed ? (
                                  <Box
                                    sx={{
                                      width: "100vw",
                                      height: "100vh",
                                    }}
                                  >
                                    <iframe
                                      src={
                                        serviceDetailed?.data?.location?.mapUrl
                                      }
                                      width="100%"
                                      height="100%"
                                      frameBorder="0"
                                    ></iframe>
                                  </Box>
                                ) : null
                              }
                            />
                          </Box>
                          <Typography
                            sx={{ pt: 2, fontSize: "16px", fontWeight: "bold" }}
                            align={"left"}
                          >
                            Время
                          </Typography>
                          <Typography
                            sx={{ fontSize: "12px" }}
                            align={"left"}
                          >{`${getDay(serviceDetailed?.data?.timeFrame?.from)} в ${getHourMinutes(serviceDetailed?.data?.timeFrame?.from)}`}</Typography>
                          <Stack
                            direction={"row"}
                            sx={{ justifyContent: "center", mt: 8 }}
                            spacing={2}
                          >
                            {
                              // serviceDetailed?.data?.paymentStatus != 'PAID' && serviceDetailed?.data?.paymentLink ?
                              <Button
                                variant={"contained"}
                                href={serviceDetailed?.data?.paymentLink}
                                sx={{
                                  flex: "1 1 auto",
                                  maxWidth: "400px",
                                }}
                              >
                                Оплатить
                              </Button>
                              // null
                            }
                          </Stack>
                          <Stack
                            direction={"row"}
                            sx={{ justifyContent: "center", mt: 2 }}
                            spacing={2}
                          >
                            {plusDays(now(), 1) <
                            serviceDetailed?.data?.timeFrame?.from ? (
                              <Button
                                variant={"outlined"}
                                onClick={() => {
                                  handleServiceEdit();
                                }}
                                sx={{
                                  flex: "1 1 auto",
                                  maxWidth: "400px",
                                }}
                              >
                                Кастомизировать
                              </Button>
                            ) : null}
                          </Stack>
                          {now() < serviceDetailed?.data?.timeFrame?.from ? (
                            <Stack
                              sx={{
                                mt: 4,
                                alignItems: "center",
                                verticalAlign: "middle",
                              }}
                            >
                              <Button
                                variant={"text"}
                                onClick={() => {
                                  handleCancelService(serviceDetailed?.data);
                                }}
                                sx={{
                                  flex: "1 1 auto",
                                  maxWidth: "400px",
                                  color: "#cd5c5c",
                                  alignItems: "center",
                                  verticalAlign: "middle",
                                }}
                              >
                                {/*<Close sx={{mr: 1}}/>*/}
                                <Typography
                                  align={"center"}
                                  sx={{ height: "100%" }}
                                >
                                  Отменить прием
                                </Typography>
                              </Button>
                            </Stack>
                          ) : null}
                        </CardContent>
                      </Box>
                    </Card>
                  ) : null
                }
              />
            </Box>
          </Stack>
        </Box>
      </Modal>
      {/* service edit form   */}
      <Modal
        open={serviceEdit.isEditing}
        sx={{ overflow: "auto", height: "100vh", zIndex: 1305 }}
      >
        <Box sx={{ position: "relative", minHeight: "100vh" }}>
          <Card elevation={1} sx={{ display: "block", minHeight: "100vh" }}>
            <Box
              sx={{
                minHeight: "100vh",
                backgroundColor: "rgb(0 0 0/0.05)",
              }}
            >
              <Stack direction={"row"} sx={{ justifyContent: "space-between" }}>
                <Box sx={{ m: 2 }}>
                  <Chip
                    sx={{ backgroundColor: "#fff" }}
                    label={"Отменить"}
                    onClick={() => {
                      setServiceEdit((prevState) => ({
                        ...prevState,
                        isEditing: false,
                      }));
                    }}
                  ></Chip>
                </Box>
                <Box sx={{ m: 2 }}>
                  <Chip
                    sx={{ backgroundColor: "#fff" }}
                    label={"Сохранить"}
                    onClick={() => {
                      handleServiceChange();
                    }}
                  ></Chip>
                </Box>
              </Stack>
              {serviceEdit?.data?.customizations?.wish?.enabled ? (
                <Box
                  sx={{
                    borderRadius: "30px",
                    backgroundColor: "rgb(255 255 255)",
                    m: "15px",
                    p: 2,
                  }}
                >
                  <Stack>
                    <Typography
                      align={"left"}
                      fontSize={"12px"}
                      sx={{ color: "rgb(0 0 0/ 0.7)", mb: 1 }}
                    >
                      Кратко опишите свои пожелания и мы придумаем оптиамальную
                      программу
                    </Typography>
                    <TextField
                      value={serviceEdit?.data?.customizations?.wish?.data}
                      multiline
                      rows={5}
                      sx={{ width: "100%" }}
                      onChange={(event) => {
                        if (!serviceEdit?.data?.customizations) {
                          setServiceEdit((prevState) => ({
                            ...prevState,
                            data: {
                              ...prevState?.data,
                              customizations: {
                                ...prevState?.data?.customizations,
                                wish: {
                                  ...serviceEdit?.data?.customizations?.wish,
                                },
                              },
                            },
                          }));
                        }
                        setServiceEdit((prevState) => ({
                          ...prevState,
                          data: {
                            ...prevState?.data,
                            customizations: {
                              ...prevState?.data?.customizations,
                              wish: {
                                ...prevState?.data?.customizations?.wish,
                                data: event.target.value,
                              },
                            },
                          },
                        }));
                      }}
                    />
                  </Stack>
                </Box>
              ) : null}
              {serviceEdit?.data?.customizations?.cosmetics?.enabled ? (
                <Box
                  sx={{
                    borderRadius: "30px",
                    backgroundColor: "rgb(255 255 255)",
                    m: "15px",
                    p: 2,
                  }}
                >
                  <Typography
                    align={"left"}
                    fontSize={"12px"}
                    sx={{ color: "rgb(0 0 0/ 0.7)", mb: 1 }}
                  >
                    Выберите косметику
                  </Typography>
                  <ItemsListV2
                    itemsLoading={false}
                    closeDetailed={() => {}}
                    items={serviceEdit?.data?.customizations?.cosmetics?.data?.map(
                      (it) => (
                        <Stack
                          direction={"row"}
                          sx={{
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                          onClick={() => {
                            setServiceEdit((prevState) => ({
                              ...prevState,
                              data: {
                                ...prevState?.data,
                                customizations: {
                                  ...prevState?.data?.customizations,
                                  cosmetics: {
                                    ...prevState?.data?.customizations
                                      ?.cosmetics,
                                    data: prevState?.data?.customizations?.cosmetics?.data?.map(
                                      (innerIt) => {
                                        innerIt.checked = false;
                                        if (innerIt?.title == it?.title) {
                                          innerIt.checked = true;
                                        }
                                        return innerIt;
                                      },
                                    ),
                                  },
                                },
                              },
                            }));
                          }}
                        >
                          <Checkbox checked={it?.checked} label={it?.title} />
                          <Typography>{it?.title}</Typography>
                        </Stack>
                      ),
                    )}
                    itemDetailed={null}
                  />
                </Box>
              ) : null}
            </Box>
          </Card>
        </Box>
      </Modal>
      {/* service edit form end  */}
      {/* user edit form   */}
      <Modal
        open={specialistEdit.isEditing}
        sx={{ overflow: "auto", height: "100vh", zIndex: 1305 }}
      >
        <Box sx={{ position: "relative", minHeight: "100vh" }}>
          <Card
            elevation={1}
            sx={{
              display: "block",
              minHeight: "100vh",
              backgroundColor: "#eee",
            }}
          >
            <Box
              sx={{
                height: "100%",
              }}
            >
              <Stack direction={"row"} sx={{ justifyContent: "space-between" }}>
                <Box sx={{ m: 2 }}>
                  <Chip
                    disabled={specialistEdit.isUpdating}
                    sx={{ backgroundColor: "#fff" }}
                    label={"Отмена"}
                    onClick={() => {
                      setSpecialistEdit(SPECIALIST_INFO_EDIT_DEFAULT_STATE);
                    }}
                  ></Chip>
                </Box>
                <Box sx={{ m: 2 }}>
                  <Chip
                    disabled={specialistEdit.isUpdating}
                    sx={{ backgroundColor: "#fff" }}
                    label={"Сохранить"}
                    onClick={() => {
                      handleSpecialistChange();
                    }}
                  ></Chip>
                </Box>
              </Stack>
              <Box
                sx={{
                  borderRadius: "30px",
                  backgroundColor: "rgb(255 255 255)",
                  m: "15px",
                  p: 2,
                }}
              >
                <Stack>
                  <Typography
                    align={"left"}
                    fontSize={"12px"}
                    sx={{ color: "rgb(0 0 0/ 0.7)" }}
                  >
                    Имя
                  </Typography>
                  <TextField
                    disabled={specialistEdit.isUpdating}
                    value={specialistEdit?.data?.firstName}
                    sx={{ width: "100%" }}
                    onChange={(event) => {
                      setSpecialistEdit((prevState) => ({
                        ...prevState,
                        data: {
                          ...prevState.data,
                          firstName: event.target.value,
                        },
                      }));
                    }}
                  />
                </Stack>
                <Stack sx={{ pt: 1 }}>
                  <Typography
                    align={"left"}
                    fontSize={"12px"}
                    sx={{ color: "rgb(0 0 0/ 0.7)" }}
                  >
                    Фамилия
                  </Typography>
                  <TextField
                    disabled={specialistEdit.isUpdating}
                    value={specialistEdit?.data?.lastName}
                    sx={{ width: "100%" }}
                    onChange={(event) => {
                      setSpecialistEdit((prevState) => ({
                        ...prevState,
                        data: {
                          ...prevState.data,
                          lastName: event.target.value,
                        },
                      }));
                    }}
                  />
                </Stack>
              </Box>
            </Box>
          </Card>
        </Box>
      </Modal>
      <BonusProgram
        open={bonusProgram?.open}
        sx={{
          zIndex: 1305,
        }}
      >
        <Box sx={{ height: "100dvh", overflowY: "auto" }}>
          <Icon
            onClick={handleBonusProgramClose}
            sx={{
              zIndex: 1306,
              // '&:hover': { cursor: 'pointer', backgroundColor: "#000", color: "#fff"},
              p: "5px",
              backgroundColor: "#eee",
              borderRadius: "50%",
              position: "absolute",
              top: "10px",
              right: "10px",
            }}
          >
            <ClearIcon />
          </Icon>
          <Box
            sx={{
              display: "flex",
              height: "100dvh",
              backgroundColor: "#fff",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                overflowY: "auto",
                height: "100%",
                p: 2,
              }}
            >
              {
                <BonusProgramMassageSpecialist
                  program={_.head(specialistInfo?.data)?.bonusProgram}
                />
              }
            </Box>
          </Box>
        </Box>
      </BonusProgram>
    </App>
  );
};

export default AccountScreen;
